import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { sectionsService } from '@/services/sectionsService';
import type { AssetTagsResponse, TagOverride, AddTagPayload } from '@/types';

// ─── State ────────────────────────────────────────────────────────────────────

interface TagsState {
  assetTags: Record<string, AssetTagsResponse>;
  loadingAssetId: string | null;
  addLoading: boolean;
  /** tagId → true while delete is in-flight */
  deleteLoading: Record<string, boolean>;
}

const initialState: TagsState = {
  assetTags: {},
  loadingAssetId: null,
  addLoading: false,
  deleteLoading: {},
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Append a new tag to the correct category array inside an AssetTagsResponse */
function appendTag(tagMap: AssetTagsResponse, tag: TagOverride): void {
  switch (tag.tag_type) {
    case 'object':      tagMap.tags.objects.push(tag);       break;
    case 'activity':    tagMap.tags.activities.push(tag);    break;
    case 'scene':       tagMap.tags.scenes.push(tag);        break;
    case 'key_point':   tagMap.tags.key_points.push(tag);   break;
    case 'ai_speaker':  tagMap.tags.ai_speakers.push(tag);  break;
    case 'person':      tagMap.tags.people.push(tag);        break;
    case 'topic':       tagMap.tags.topics.push(tag);        break;
    case 'description': tagMap.tags.descriptions.push(tag); break;
  }
}

/** Remove a tag by id from every category array */
function removeTag(tagMap: AssetTagsResponse, tagId: string): void {
  const cats = Object.values(tagMap.tags) as TagOverride[][];
  for (const arr of cats) {
    const idx = arr.findIndex((t) => t.id === tagId);
    if (idx !== -1) { arr.splice(idx, 1); break; }
  }
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchAssetTags = createAsyncThunk(
  'tags/fetchAssetTags',
  async (assetId: string, { rejectWithValue }) => {
    try {
      const res = await sectionsService.getAssetTags(assetId);
      return res.data;
    } catch {
      return rejectWithValue(assetId);
    }
  }
);

export const addAssetTag = createAsyncThunk(
  'tags/addAssetTag',
  async (
    { assetId, payload }: { assetId: string; payload: AddTagPayload },
    { rejectWithValue }
  ) => {
    try {
      const res = await sectionsService.addAssetTag(assetId, payload);
      return { assetId, tag: res.data };
    } catch {
      return rejectWithValue('Failed to add tag.');
    }
  }
);

export const deleteAssetTag = createAsyncThunk(
  'tags/deleteAssetTag',
  async (
    { assetId, tagId }: { assetId: string; tagId: string },
    { rejectWithValue }
  ) => {
    try {
      await sectionsService.deleteAssetTag(assetId, tagId);
      return { assetId, tagId };
    } catch {
      return rejectWithValue('Failed to remove tag.');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssetTags.pending, (state, action) => {
        state.loadingAssetId = action.meta.arg;
      })
      .addCase(fetchAssetTags.fulfilled, (state, action) => {
        state.loadingAssetId = null;
        const data = action.payload as AssetTagsResponse;
        // Ensure descriptions array exists even if API omits it
        if (!data.tags.descriptions) data.tags.descriptions = [];
        state.assetTags[data.asset_id] = data;
      })
      .addCase(fetchAssetTags.rejected, (state) => {
        state.loadingAssetId = null;
      });

    builder
      .addCase(addAssetTag.pending, (state) => {
        state.addLoading = true;
      })
      .addCase(addAssetTag.fulfilled, (state, action) => {
        state.addLoading = false;
        const { assetId, tag } = action.payload as { assetId: string; tag: TagOverride & { timestamp_ms_start?: number | null } };
        // POST response uses timestamp_ms_start; normalize to timestamp_ms used by GET
        const normalized: TagOverride = {
          ...tag,
          timestamp_ms: tag.timestamp_ms_start ?? tag.timestamp_ms ?? null,
        };
        const existing = state.assetTags[assetId];
        if (existing) appendTag(existing, normalized);
      })
      .addCase(addAssetTag.rejected, (state) => {
        state.addLoading = false;
      });

    builder
      .addCase(deleteAssetTag.pending, (state, action) => {
        state.deleteLoading[action.meta.arg.tagId] = true;
      })
      .addCase(deleteAssetTag.fulfilled, (state, action) => {
        const { assetId, tagId } = action.payload as { assetId: string; tagId: string };
        delete state.deleteLoading[tagId];
        const existing = state.assetTags[assetId];
        if (existing) removeTag(existing, tagId);
      })
      .addCase(deleteAssetTag.rejected, (state, action) => {
        delete state.deleteLoading[action.meta.arg.tagId];
      });
  },
});

export default tagsSlice.reducer;
